import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { pipe, Array as A, Record as R } from 'effect';
import type { List } from 'immutable';

import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import Dialog from '@/components/mui/Dialog';
import Spinner from '@/components/mui/CircularProgressWithBackdrop';
import TextFieldRhf from '@/components/mui/Form/TextField/TextFieldRhf';
import EmailAutocomplete from '@/pages/common/Dialog/DialogSendByEmail/components/EmailAutocomplete';
import type { DialogUser } from '@/pages/common/Dialog/components/EmailTag';
import { sendByEmailFormSchema, type SendByEmailFormData } from '@/pages/common/Dialog/DialogSendByEmail/schema';

import { useEmailAutocomplete, useSendDocumentByEmailMutation } from '@/domain/documents/queries';
import { userIdSelector, userNamesSelector } from '@/domain/env';
import { chatParticipantsSelector } from '@/domain/chat';

import { parseJSON, storage } from '@/lib/storage';

// The chat participant store is an untyped Immutable List<Record>. Narrowed
// structurally at the selector boundary — the same projection
// `useChatUserDirectory` uses — so no `any` reaches the autocomplete.
interface ChatParticipantRecord {
  get(key: 'userId' | 'username' | 'picture'): string;
}

interface DialogSendByEmailProps {
  documentIds?: string[];
  onClose: () => void;
  linkID?: string;
}

// Stable identity for the empty case: a fresh `[]` here would give the `emails`
// memo below a new dependency on every render.
const NO_EMAILS: string[] = [];

// Whatever a previous session left in localStorage: it can be absent, malformed,
// or valid JSON of the wrong shape. Anything but a list of strings resolves to an
// empty one rather than reaching the code below, which spreads it.
const emailSuggestionSchema = z.array(z.string()).catch([]);

const DialogSendByEmail: React.FC<DialogSendByEmailProps> = ({ documentIds = [], onClose, linkID = '' }) => {
  const { formatMessage } = useIntl();
  const { companyId } = useParams<{ companyId: string }>();
  const sendMutation = useSendDocumentByEmailMutation();
  const { data: autocomplete, isLoading } = useEmailAutocomplete(companyId!, documentIds, linkID);
  const emailsFromBE = autocomplete?.emails ?? NO_EMAILS;

  const { firstName, lastName } = useSelector(userNamesSelector);
  const email = useSelector(userIdSelector);
  const participants = useSelector(chatParticipantsSelector) as List<ChatParticipantRecord>;

  // Read once per mount: the list is written on a successful send, by which point
  // this dialog is closing. Parsing on every render handed `emails` below a new
  // array identity each time and made its memo a no-op.
  const [emailSuggestion] = useState<string[]>(() =>
    emailSuggestionSchema.parse(parseJSON(storage().emailSuggestion().get())),
  );

  const users = useMemo(
    () =>
      R.fromIterableBy(
        A.map(
          participants.toArray(),
          (participant): DialogUser => ({
            userId: participant.get('userId'),
            username: participant.get('username'),
            picture: participant.get('picture'),
          }),
        ),
        (user) => user.userId,
      ),
    [participants],
  );

  const emails = useMemo(
    () => pipe([...R.keys(users), ...emailSuggestion, ...emailsFromBE], A.dedupe),
    [users, emailSuggestion, emailsFromBE],
  );

  const toLabel = formatMessage({ id: 'form.dialog.sendByEmail.to', defaultMessage: 'To' });
  const ccLabel = formatMessage({ id: 'form.dialog.sendByEmail.cc', defaultMessage: 'CC' });
  const subjectLabel = formatMessage({ id: 'form.dialog.sendByEmail.subject', defaultMessage: 'Subject' });
  const descriptionLabel = formatMessage({
    id: 'form.dialog.sendByEmail.description',
    defaultMessage: 'Description',
  });

  const defaultValues = useMemo<SendByEmailFormData>(
    () => ({
      to: [],
      cc: [],
      subject: formatMessage({
        id: 'form.dialog.sendByEmail.subject.default',
        defaultMessage: 'A document has been forwarded to you',
      }),
      description: formatMessage(
        {
          id: 'form.dialog.sendByEmail.description.default',
          defaultMessage:
            'Hi,\nI have something for you to review.\nTo see the document open the attached file.\n\nBest Regards\n{username}',
        },
        { username: `${firstName} ${lastName} (${email})` },
      ),
    }),
    [firstName, lastName, email, formatMessage],
  );

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<SendByEmailFormData>({
    resolver: zodResolver(sendByEmailFormSchema),
    // The legacy form ran with `touchOnChange: true` — errors showed as you
    // typed, without waiting for a blur.
    mode: 'onChange',
    defaultValues,
  });

  const to = watch('to');
  const cc = watch('cc');

  // An address already picked in either field drops out of both dropdowns.
  const options = useMemo(() => pipe(emails, A.difference([...to, ...cc])), [to, cc, emails]);

  const onSubmit = handleSubmit((values) => {
    const newEmails = pipe([...values.to, ...values.cc], A.dedupe, A.difference(emails));

    // The untouched default subject is sent prefixed; a subject the user wrote
    // themselves goes as typed.
    const subject =
      values.subject === defaultValues.subject
        ? `${formatMessage({
            id: 'form.dialog.sendByEmail.subject.doNotReply',
            defaultMessage: '[Do not reply]',
          })} ${values.subject}`
        : values.subject;

    sendMutation.mutate(
      {
        body: {
          email_to: values.to,
          cc: values.cc,
          subject,
          message: values.description,
          linkID,
          document_ids: documentIds,
        },
      },
      {
        onSuccess: () => {
          // Hand-typed addresses are remembered for the next send. Written here
          // rather than in the mutation hook: they never travel with the
          // request, so they are not part of its variables.
          if (newEmails.length) {
            storage().emailSuggestion().set(JSON.stringify([...emailSuggestion, ...newEmails]));
          }
          onClose();
        },
      },
    );
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={formatMessage({ id: 'form.dialog.sendByEmail.title', defaultMessage: 'Send a new email' })}
      maxWidth="sm"
      withActions={false}
      withContent={false}
    >
      {isLoading && <Spinner isOpen BackdropProps={{ sx: { position: 'absolute' } }} />}
      <form onSubmit={onSubmit} noValidate>
        <DialogContent>
          <Stack direction="column" alignItems="center" spacing={1}>
            <Controller
              name="to"
              control={control}
              render={({ field, fieldState }) => (
                <EmailAutocomplete
                  id="sendByEmail-to"
                  label={toLabel}
                  emailOptions={options}
                  users={users}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  errorMessage={fieldState.error?.message}
                  required
                  autoFocus
                />
              )}
            />
            <Controller
              name="cc"
              control={control}
              render={({ field, fieldState }) => (
                <EmailAutocomplete
                  id="sendByEmail-cc"
                  label={ccLabel}
                  emailOptions={options}
                  users={users}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
            <TextFieldRhf
              name="subject"
              control={control}
              label={subjectLabel}
              placeholder={subjectLabel}
              required
              type="text"
              fullWidth
            />
            <TextFieldRhf
              name="description"
              control={control}
              label={descriptionLabel}
              placeholder={descriptionLabel}
              required
              multiline
              rows={7}
              type="text"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="text">
            {formatMessage({ id: 'form.dialog.sendByEmail.button.cancel', defaultMessage: 'Cancel' })}
          </Button>
          <Button
            variant="contained"
            type="submit"
            loading={sendMutation.isLoading}
            disabled={sendMutation.isLoading || !isValid}
          >
            {formatMessage({ id: 'button.send', defaultMessage: 'Send' })}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DialogSendByEmail;
